import { isValidElementType } from 'react-is';
import { filter } from './fn';

/**
 * Wraps parametrized HOC function allowing to call it both with param, or without it, as follows:
 * - HOC(params)(WrappedComponent)
 * - HOC(WrappedComponents)
 */
export const allowParamOmit = HOC =>
  arg => (isValidElementType(arg) ? HOC()(arg) : HOC(arg));


/**
 * Wraps component by passing:
 * `finalProps` that can no longer be changed and
 * `defaultProps` that can be overridden
 */
export const defineProps = (finalProps, defaultProps) =>
  (WrappedComponent) => {
    const RedefinedComponent = props => (
      <WrappedComponent
        {...defaultProps}
        {...props}
        {...finalProps}
      />
    );

    if (process.env.NODE_ENV !== 'production') {
      RedefinedComponent.displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
      RedefinedComponent.propTypes = {
        ...filter(WrappedComponent.propTypes, (_, propName) => !(propName in (finalProps || {}))),
      };
    }

    return RedefinedComponent;
  };


/**
 * A shortcut used by factories designed via same interface,
 * passes final prop childComponents
 */
export const defineFactoryComponents = childComponents => defineProps({ childComponents });


const SecretPrevProps = '__prevRenderProps';

/**
 * Wrapper around getDerivedStateFromProps to only react on props change and ignore setState calls
 */
export const ignoreState = getDerivedStateFromProps => (nextProps, prevState) => {
  if (prevState[SecretPrevProps] !== nextProps) {
    return {
      ...getDerivedStateFromProps(nextProps, prevState),
      [SecretPrevProps]: nextProps,
    };
  }
  return null;
};
